import { pdfjs as PDFJS } from 'react-pdf';
import _keyBy from 'lodash/keyBy';
import { LABELLING_TYPE } from '../../common/constant/constant';
import { TASK_MODE } from '../../common/task-state/constants';
import type { GenericAnnotationReviewDataT, LabellingValidationsT } from '../../common/types/types';
import type { RangeSpanT } from '../../common/types/types';
import { type AnnotationLabelT, STORE_TYPE } from '../../common/types/types';
import { type TaskStateT } from '../../common/types/types';
import { createAnnotationMLStore, createAnnotationReviewData, createAnnotationStore, getAnnotationReviewData, getOutputReviewData, isRejectedMode } from '../../common/utils/utils';
import { handleShowHideAllAnnotations } from './reducers/utils/utils';
import { VERSION } from './constants';
import type { AnnotationReviewStoreT, AnnotationStoreT, FrameStateT, ImageInputDataT, ImageOutputLabelledDataT, InitialLabelStateT, InputDataT, KeyFrameT, KeyFrameType, OutputAnnotationT, OutputRelationshipT, PDFOutputLabelledDataT, SkeletonAnnotationT, VideoInputDataT, VideoOutputLabelledDataT } from './types';
import { type InputFrameT } from './types';

// TO do As fractal does not support assetURl, using CDN for now until gets a solution
PDFJS.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS.version}/pdf.worker.min.mjs`;
export function getOutputAnnotations(annotationStore: AnnotationStoreT): OutputAnnotationT[] {
  return Object.keys(annotationStore).reduce((annotations, annotationId) => {
    const annotation = annotationStore[annotationId];
    // @ts-expect-error todo(ts-migration) TS2339 Property 'annotationLabel' does not exist on type 'AnnotationT | undefined'.
    const {
      annotationLabel
    } = annotation;
    // Style not required in output data
    const {
      style: _style,
      ...annotationLabelData
    } = annotationLabel;
    // @ts-expect-error todo(ts-migration) TS2322 Type '{ id: string; annotationLabel: any; } | { id: string; annotationLabel: any; annotationType: "SEGMENTATION"; data: BrushDataT; metadata?: ({ answers?: AnswerT | undefined; mlData?: AnnotationMLDa...
    const outputAnnotation: OutputAnnotationT = {
      ...annotationStore[annotationId],
      id: annotationId,
      annotationLabel: annotationLabelData
    };
    // @ts-expect-error missing null check
    annotations.push(outputAnnotation);
    return annotations;
  }, []);
}
export const getInitialVideoFrames = (frames: InputFrameT[], annotationLabelMap: {
  [x: string]: AnnotationLabelT | undefined | null;
}, taskState: TaskStateT) => {
  let initialFrames = getInitialFrames(frames, annotationLabelMap);
  const showMLAnnotations = shouldShowMLAnnotations(taskState, initialFrames);
  if (!showMLAnnotations) {
    initialFrames = handleShowHideAllAnnotations(true, STORE_TYPE.ML_ANNOTATION_STORE, initialFrames);
  }
  return {
    showMLAnnotations,
    initialFrames
  };
};
export const createAnnotationLabelMap = annotationLabels => _keyBy(annotationLabels, 'title');
export const getFramesFromInputData = (inputData: InputDataT, annotationLabels: AnnotationLabelT[]): InitialLabelStateT | undefined | null => {
  const selectedAnnotationLabel = annotationLabels.length > 0 ? annotationLabels[0] : null;
  const annotationLabelMap = createAnnotationLabelMap(annotationLabels);
  const {
    taskState,
    reviewData
  } = inputData;
  const shouldCreateAnnotationReviewStore = taskState.taskModes.includes(TASK_MODE.REVIEW) && (!taskState.reviewModeVersion || taskState.reviewModeVersion === 1); // should not create annotation review store for review mode v2
  // @ts-expect-error todo(ts-migration) TS2339 Property 'imageUrl' does not exist on type 'InputDataT'.
  if (inputData.imageUrl) {
    // @ts-expect-error todo(ts-migration) TS2339 Property 'imageUrl' does not exist on type 'InputDataT'.
    const {
      imageUrl,
      annotations,
      metadata,
      annotationMLData,
      superpixelSegmentsNumbers
    } = inputData;
    let annotationMLStore;
    if (annotationMLData) {
      annotationMLStore = createAnnotationMLStore(annotationMLData, annotationLabelMap);
    }
    let annotationConsensusStore;
    const annotationConsensusData = (inputData as ImageInputDataT).annotationConsensusData;
    if (annotationConsensusData) {
      // for now, we are using the same createAnnotationMLStore for consensus data
      annotationConsensusStore = createAnnotationMLStore(annotationConsensusData, annotationLabelMap);
    }
    let frames = [{
      imageUrl,
      annotationStore: createAnnotationStore(annotations, annotationLabelMap),
      annotationMLStore,
      annotationConsensusStore
    }];
    const showMLAnnotations = shouldShowMLAnnotations(inputData.taskState, frames);
    if (!showMLAnnotations) {
      // @ts-expect-error todo(ts-migration) TS2322 Type 'FrameStateT[]' is not assignable to type '{ imageUrl: any; annotationStore: any; annotationMLStore: any; }[]'.
      frames = handleShowHideAllAnnotations(true, STORE_TYPE.ML_ANNOTATION_STORE, frames);
    }
    const showConsensusAnnotations = shouldShowMLAnnotations(inputData.taskState, frames);
    if (!showConsensusAnnotations) {
      // @ts-expect-error todo(ts-migration) TS2322 Type 'FrameStateT[]' is not assignable to type '{ imageUrl: any; annotationStore: any; annotationMLStore: any; annotationConsensusStore: any; }[]'.
      frames = handleShowHideAllAnnotations(true, STORE_TYPE.CONSENSUS_ANNOTATION_STORE, frames);
    }
    return {
      dataUrl: imageUrl,
      frames,
      validations: {
        invalidSource: Boolean(metadata?.invalidSource),
        labelValidation: {
          isRequiredLabelAnnotated: true,
          reasons: []
        }
      },
      //Keeping createAnnotationReviewStore decouple with TASK_MODE, because every component doesn't have access of TASK_MODE.
      annotationReviewStore: shouldCreateAnnotationReviewStore ? createAnnotationReviewStore(frames, reviewData) : {},
      selectedAnnotationLabel,
      showMLAnnotations,
      annotationLabels,
      superpixel: {
        superpixelSegmentsNumbers
      }
    };
    // @ts-expect-error todo(ts-migration) TS2339 Property 'videoUrl' does not exist on type 'InputDataT'.
  } else if (inputData.videoUrl) {
    const {
      videoUrl,
      mediaFileReference,
      frames,
      metadata,
      frameRate,
      timeDisplayMode,
      multiVideoList
    } = (inputData as VideoInputDataT);
    const {
      showMLAnnotations,
      initialFrames
    } = getInitialVideoFrames(frames, annotationLabelMap, taskState);
    return {
      dataUrl: videoUrl,
      mediaFileReference,
      frames: initialFrames,
      frameRate,
      validations: {
        invalidSource: Boolean(metadata?.invalidSource),
        labelValidation: {
          isRequiredLabelAnnotated: true,
          reasons: []
        }
      },
      annotationReviewStore: shouldCreateAnnotationReviewStore ? createAnnotationReviewStore(initialFrames, reviewData) : {},
      selectedAnnotationLabel,
      showMLAnnotations,
      annotationLabels,
      timeDisplayMode,
      multiVideoList
    };
  } else if ('pdfUrl' in inputData) {
    const {
      pdfUrl,
      metadata,
      pdfNumOfPages,
      frames,
      consensusDataFrames
    } = inputData;
    const updatedFrames: InputFrameT[] = frames.length > 0 ? frames : new Array<InputFrameT>(pdfNumOfPages).fill({
      annotations: [],
      imageUrl: ''
    });
    let initialFrames = getInitialFramesWithConsensus(updatedFrames, annotationLabelMap, consensusDataFrames);
    const showMLAnnotations = shouldShowMLAnnotations(inputData.taskState, initialFrames);
    if (!showMLAnnotations) {
      initialFrames = handleShowHideAllAnnotations(true, STORE_TYPE.ML_ANNOTATION_STORE, initialFrames);
    }
    const showConsensusAnnotations = shouldShowMLAnnotations(inputData.taskState, initialFrames);
    if (!showConsensusAnnotations) {
      initialFrames = handleShowHideAllAnnotations(true, STORE_TYPE.CONSENSUS_ANNOTATION_STORE, initialFrames);
    }
    return {
      dataUrl: pdfUrl,
      frames: initialFrames,
      validations: {
        invalidSource: Boolean(metadata?.invalidSource),
        labelValidation: {
          isRequiredLabelAnnotated: true,
          reasons: []
        }
      },
      annotationReviewStore: shouldCreateAnnotationReviewStore ? createAnnotationReviewStore(initialFrames, reviewData) : {},
      fileType: LABELLING_TYPE.PDF,
      selectedAnnotationLabel,
      showMLAnnotations,
      annotationLabels
    };
  }
  return null;
};
export const createAnnotationReviewStore = (frames: FrameStateT[], reviewData?: GenericAnnotationReviewDataT[] | null): AnnotationReviewStoreT => {
  const annotationReviewStore = {};
  frames.forEach(frame => {
    Object.keys(frame.annotationStore).forEach(annotationId => {
      //if annotation already exist in annotationReviewStore, incase of VIDEO_LABELLING
      if (annotationReviewStore[annotationId]) {
        return annotationReviewStore;
      }
      const annotation = frame.annotationStore[annotationId];
      let annotationReviewData = getAnnotationReviewData(annotationId, reviewData);
      if (!annotationReviewData) {
        // @ts-expect-error todo(ts-migration) TS2345 Argument of type '{ id: string; } | { id: string; annotationType: "SEGMENTATION"; data: BrushDataT; metadata?: ({ answers?: AnswerT | undefined; mlData?: AnnotationMLDataT | undefined; originalId?: st...
        annotationReviewData = createAnnotationReviewData({
          ...annotation,
          id: annotationId
        });
      }
      annotationReviewStore[annotationId] = annotationReviewData;
    });
  });
  return annotationReviewStore;
};
const getInitialFrames = (frames: InputFrameT[], annotationLabelMap: {
  [x: string]: AnnotationLabelT | undefined | null;
}): FrameStateT[] => {
  return frames.map(frame => {
    const {
      annotations,
      imageUrl
    } = frame;
    return {
      imageUrl,
      annotationStore: createAnnotationStore(annotations, annotationLabelMap)
    };
  });
};
const getInitialFramesWithConsensus = (frames: InputFrameT[], annotationLabelMap: {
  [x: string]: AnnotationLabelT | undefined | null;
}, consensusDataFrames?: InputFrameT[]): FrameStateT[] => {
  return frames.map((frame, index) => {
    const {
      annotations,
      imageUrl
    } = frame;
    let annotationConsensusStore;
    if (consensusDataFrames?.[index]) {
      annotationConsensusStore = createAnnotationMLStore(consensusDataFrames[index]?.annotationConsensusData ?? [], annotationLabelMap);
    }
    return {
      imageUrl,
      annotationStore: createAnnotationStore(annotations, annotationLabelMap),
      annotationConsensusStore
    };
  });
};
export function createImageOutput(state: {
  dataUrl?: string | null;
  frames: {
    annotationStore: AnnotationStoreT;
    imageUrl?: string;
  }[];
  validations: LabellingValidationsT;
  annotationReviewStore?: AnnotationReviewStoreT;
  relationships?: OutputRelationshipT[];
}): ImageOutputLabelledDataT {
  // @ts-expect-error todo(ts-migration) TS2339 Property 'annotationStore' does not exist on type 'FrameStateT | undefined'.
  const {
    annotationStore
  } = state.frames[0];
  return {
    version: VERSION,
    labellingType: LABELLING_TYPE.IMAGE,
    metadata: {
      invalidSource: state.validations.invalidSource
    },
    imageUrl: state.dataUrl ?? '',
    annotations: getOutputAnnotations(annotationStore),
    reviewData: getOutputReviewData(state.annotationReviewStore ?? {}),
    relationships: state.relationships
  };
}
export function createVideoOutput(state: {
  dataUrl?: string | null;
  frames: {
    annotationStore: AnnotationStoreT;
    imageUrl?: string;
  }[];
  validations: LabellingValidationsT;
  annotationReviewStore?: AnnotationReviewStoreT;
  relationships?: OutputRelationshipT[];
}): VideoOutputLabelledDataT {
  return {
    version: VERSION,
    labellingType: LABELLING_TYPE.VIDEO,
    metadata: {
      invalidSource: state.validations.invalidSource
    },
    videoUrl: state.dataUrl ?? '',
    frames: state.frames.map(frame => {
      const {
        annotationStore,
        imageUrl
      } = frame;
      return {
        imageUrl: imageUrl ?? '',
        annotations: getOutputAnnotations(annotationStore)
      };
    }),
    reviewData: getOutputReviewData(state.annotationReviewStore ?? {}),
    relationships: state.relationships
  };
}
export function createPDFOutput(state: {
  dataUrl?: string | null;
  frames: {
    annotationStore: AnnotationStoreT;
    imageUrl?: string;
  }[];
  validations: LabellingValidationsT;
  annotationReviewStore?: AnnotationReviewStoreT;
}): PDFOutputLabelledDataT {
  return {
    version: VERSION,
    labellingType: LABELLING_TYPE.PDF,
    metadata: {
      invalidSource: state.validations.invalidSource
    },
    // @ts-expect-error missing null check
    pdfUrl: state.dataUrl,
    frames: state.frames.map(frame => {
      const {
        annotationStore,
        imageUrl
      } = frame;
      return {
        imageUrl: imageUrl ?? '',
        annotations: getOutputAnnotations(annotationStore)
      };
    }),
    reviewData: getOutputReviewData(state.annotationReviewStore ?? {})
  };
}
export const initializeTaskState = (initialState: InitialLabelStateT | undefined | null, initialTaskState: TaskStateT): TaskStateT => {
  const taskState = initialTaskState;
  if (initialState?.annotationReviewStore && isRejectedMode(initialState.annotationReviewStore)) {
    if (!taskState.taskModes.includes(TASK_MODE.REJECTED)) {
      taskState.taskModes.push(TASK_MODE.REJECTED);
    }
  }
  return taskState;
};
export const shouldShowMLAnnotations = (taskState: TaskStateT, frames: FrameStateT[]): boolean => {
  if (taskState.taskModes.includes(TASK_MODE.EDIT) && taskState.readOnly === false) {
    if (!checkIfAnnotationIsPresent(frames)) {
      return true;
    }
  }
  return false;
};
const checkIfAnnotationIsPresent = (frames: FrameStateT[]): boolean => {
  return (frames || []).some(frame => {
    return Object.keys(frame.annotationStore).length > 0;
  });
};
export const getFrameRangeForAnnotation = <Annotation,>(frames: {
  annotationStore: {
    [id: string]: Annotation;
  };
}[], annotationId: string) => {
  const startFrameIndex = frames.findIndex(frame => {
    if (frame.annotationStore[annotationId]) {
      return true;
    }
  });
  const endFrameIndex = frames.findLastIndex(frame => {
    if (frame.annotationStore[annotationId]) {
      return true;
    }
  });
  return {
    startFrameIndex,
    endFrameIndex
  };
};
export const isValidInputTimeFormatController = (timeInput: string, maxFrames: number, frame: number) => {
  if (!timeInput) return false;

  // validate frame is within range
  if (frame > maxFrames - 1) return false;

  // Split by '.' to separate time part and milliseconds
  const [timePart, msPart] = timeInput.split('.');

  // Validate milliseconds exist and are 3 digits
  if (!msPart || msPart.length !== 3 || isNaN(Number(msPart)) || Number(msPart) < 0) {
    return false;
  }

  // Split time part by ':' to get components (could be HH:MM:SS, MM:SS, or SS)
  const timeComponents = timePart?.split(':') ?? [];

  // Validate we have at least 1 component (seconds) and at most 3 (hours:minutes:seconds)
  if (timeComponents.length === 0 || timeComponents.length > 3) {
    return false;
  }

  // Validate each component
  for (let i = 0; i < timeComponents.length; i++) {
    const component = timeComponents[i];

    // Safety check for undefined component
    if (!component) {
      return false;
    }

    // Validate component is numeric and non-negative
    if (isNaN(Number(component)) || Number(component) < 0) {
      return false;
    }

    // Determine if this is the hours component (first component when there are 3)
    const isHoursComponent = timeComponents.length === 3 && i === 0;
    if (isHoursComponent) {
      // Hours: must be at least 2 digits, can be more
      if (component.length < 2) {
        return false;
      }
    } else {
      // Minutes and seconds: must be exactly 2 digits and < 60
      if (component.length !== 2 || Number(component) >= 60) {
        return false;
      }
    }
  }
  return true;
};
export const isEdgeAlreadyExistingInSkeletonAnnotation = (skeletonAnnotation: SkeletonAnnotationT, newEdge: {
  startNode: string;
  endNode: string;
}) => {
  const isEdgeAlreadyAdded = skeletonAnnotation.data.edges.find(edge => {
    if (newEdge.startNode === edge.startNode && newEdge.endNode === edge.endNode) {
      return true;
    }
    if (newEdge.startNode === edge.endNode && newEdge.endNode === edge.startNode) {
      return true;
    }
    return false;
  });
  return Boolean(isEdgeAlreadyAdded);
};
export const setItemInStorage = (key: string, value: string, isPersistent: boolean) => {
  isPersistent ? global.localStorage.setItem(key, value) : global.sessionStorage.setItem(key, value);
};
export const getItemFromStorage = (key: string, isPersistent: boolean) => {
  const item = isPersistent ? global.localStorage.getItem(key) : global.sessionStorage.getItem(key);
  return item || '{}';
};
export const getOverallFrameRange = (annotationSequence: Map<string, RangeSpanT>): {
  overallStart: number;
  overallEnd: number;
} => {
  let overallStart = Number.MAX_SAFE_INTEGER;
  let overallEnd = 0;
  annotationSequence.forEach(range => {
    overallStart = Math.min(overallStart, range.start);
    overallEnd = Math.max(overallEnd, range.end);
  });
  return {
    overallStart,
    overallEnd
  };
};
export const processFramesInChunks = async <T,>(startFrame: number, endFrame: number, chunkSize: number, processChunk: (chunkStart: number, chunkEnd: number) => Promise<T | null>): Promise<T[]> => {
  const results: T[] = [];
  for (let chunkStart = startFrame; chunkStart <= endFrame; chunkStart += chunkSize) {
    const chunkEnd = Math.min(chunkStart + chunkSize - 1, endFrame);
    const chunkResult = await processChunk(chunkStart, chunkEnd);
    if (chunkResult) {
      results.push(chunkResult);
    }
  }
  return results;
};
export function createKeyFrame(isKeyFrame: boolean, type: KeyFrameType = 'MANUAL', source?: string): KeyFrameT {
  return {
    isKeyFrame,
    type,
    source
  };
}